import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import omit from 'lodash.omit';

import withModal from '../../../../components/global/modal/withModal';
import { UPDATE_ROSTER_IDENTITY_MODAL } from '../../../../constants/modals';
import { MeQuery } from '../../../../graphql/queries/account';
import RosterIdentityUpdate from './RosterIdentityUpdate';
import { photoUploadSuccess } from '../../../../actions/photo-upload';
import getIdentityAlbumFolderTags, {
  getIdentityAlbumFolderTagsQuery,
} from '../../../temp/views/onboarding/TagSelection/graphql/queries/getIdentityAlbumFolderTags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../constants/album-folder-tags';
import { isUniformAlbumFolderTagConfigurable } from '../../../temp/views/onboarding/TagSelection/helpers';
import { AlbumFolderTags } from '../../../../graphql/queries/album-folder-tags';
import { getTagsByType } from '../../../../util/album-folder-tags';
import { IDENTITY_GENDER } from '../../../../constants/identity';
import { getRosterIdentityMainLinkedIdentity } from '../../../../util/identity';

const propTypes = {
  actionProps: PropTypes.shape({
    rosterIdentity: PropTypes.shape({
      id: PropTypes.string,
    }),
    identityId: PropTypes.string,
    onRosterIdentityCreated: PropTypes.func,
    onRosterIdentityUpdated: PropTypes.func,
    defaultEditAddress: PropTypes.bool,
    albumId: PropTypes.string,
    albumFolderId: PropTypes.string,
    isSubscription: PropTypes.bool,
    organizationRefIdEnabled: PropTypes.bool,
    autoFocus: PropTypes.string,
    selectedIdentity: PropTypes.shape({
      id: PropTypes.string,
      claimed: PropTypes.bool,
    }),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        uniform_enabled: PropTypes.bool,
        uniform_numbered: PropTypes.bool,
        levels: PropTypes.array,
        gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)),
      }),
    ),
  }),
};

const RosterIdentityUpdateContainer = ({
  actionProps: {
    rosterIdentity,
    identityId,
    onRosterIdentityCreated,
    onRosterIdentityUpdated,
    defaultEditAddress,
    albumId,
    albumFolderId,
    albumFolderSelectionEnabled,
    albumSelectionEnabled,
    isSubscription,
    organizationRefIdEnabled,
    autoFocus,
    selectedIdentity,
    tags,
    identityAlbumFolderTags,
  } = {},
  albumSubtype,
  pictureDayEnabled,
  ...props
}) => (
  <RosterIdentityUpdate
    rosterIdentity={rosterIdentity}
    identityId={identityId}
    defaultEditAddress={defaultEditAddress}
    albumId={albumId}
    onRosterIdentityCreated={onRosterIdentityCreated}
    onRosterIdentityUpdated={onRosterIdentityUpdated}
    albumFolderId={albumFolderId}
    albumFolderSelectionEnabled={albumFolderSelectionEnabled}
    albumSelectionEnabled={albumSelectionEnabled}
    isSubscription={isSubscription}
    organizationRefIdEnabled={organizationRefIdEnabled}
    autoFocus={autoFocus}
    selectedIdentity={selectedIdentity}
    tags={tags}
    identityAlbumFolderTags={identityAlbumFolderTags}
    albumSubtype={albumSubtype}
    pictureDayEnabled={pictureDayEnabled}
    {...omit(props, 'actionProps')}
  />
);

RosterIdentityUpdateContainer.propTypes = propTypes;

const withUserData = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
});

const mapDispatchToProps = {
  photoUploadSuccess,
};

const withAlbumFolderTags = graphql(AlbumFolderTags, {
  options: ({ actionProps }) => ({
    variables: {
      id: actionProps.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading: loadingTags } = {} }) => ({
    tags: getTagsByType(
      ALBUM_FOLDER_TAG_TYPE.UNIFORM,
      albumFolder?.tags?.records,
    ).filter(isUniformAlbumFolderTagConfigurable),
    loadingTags,
    albumSubtype: albumFolder?.album_subtype,
    pictureDayEnabled: albumFolder?.picture_day_enabled,
  }),
  skip: ({ actionProps }) => !actionProps?.albumFolderId,
});

const getIdentityAlbumFolderTagsOptions = graphql(
  getIdentityAlbumFolderTagsQuery,
  {
    options: ({
      actionProps: { identityId, rosterIdentity, albumFolderId },
    }) => {
      const mainLinkedIdentity =
        getRosterIdentityMainLinkedIdentity(rosterIdentity);
      return {
        variables: {
          identityId: identityId ?? mainLinkedIdentity.identity?.id,
          albumFolderId,
        },
      };
    },
    props: ({ data }) => ({
      loadingTags: data?.loading,
      identityAlbumFolderTags: data?.identity?.album_folder_tags?.records ?? [],
    }),
    skip: ({ actionProps: { rosterIdentity, albumFolderId } }) =>
      !rosterIdentity || !albumFolderId,
  },
);

export default withModal(
  compose(
    withUserData,
    getIdentityAlbumFolderTags,
    withAlbumFolderTags,
    getIdentityAlbumFolderTagsOptions,
    connect(null, mapDispatchToProps),
  )(RosterIdentityUpdateContainer),
  {
    name: UPDATE_ROSTER_IDENTITY_MODAL,
    contentLabel: 'Update Member',
    size: 'md',
    type: 'full',
    shouldCloseOnOverlayClick: false,
  },
);
